import type { RenderContext, RenderContextKind } from '../../elements/types';
import { assertSupportedSwarmTypes, supportsSwarmType } from '../../swarmCompatibility';
import type { BindingId, RuntimeResultLinkDescriptor } from './types';

type RuntimeBindingCompatibilityMetadata = Pick<
    RuntimeResultLinkDescriptor,
    'supportedContextKinds' | 'supportedSwarmTypes'
>;

type MeasuringPointSwarmType = Extract<
    RenderContext,
    { kind: 'measuring-point' }
>['measuringPoint']['swarmType'];

export function assertRuntimeBindingCompatibilityMetadata(
    bindingId: BindingId,
    metadata: RuntimeBindingCompatibilityMetadata
): void {
    if (metadata.supportedContextKinds.length === 0) {
        throw new Error(`Expected "${bindingId}" to define supported context kinds.`);
    }

    assertSupportedSwarmTypes(metadata.supportedSwarmTypes, `${bindingId}.supportedSwarmTypes`);

    if (
        metadata.supportedSwarmTypes !== null &&
        (!metadata.supportedContextKinds.includes('measuring-point') ||
            metadata.supportedContextKinds.includes('project'))
    ) {
        throw new Error(
            `Expected SWARM-specific runtime binding "${bindingId}" to support only measuring-point context.`
        );
    }
}

export function assertRuntimeBindingSupportsContextKind(
    bindingId: BindingId,
    metadata: RuntimeBindingCompatibilityMetadata,
    contextKind: RenderContextKind
): void {
    if (!metadata.supportedContextKinds.includes(contextKind)) {
        throw new Error(
            `Runtime binding "${bindingId}" is not compatible with ${contextKind} context.`
        );
    }
}

function getUnsupportedSwarmType(
    bindingId: BindingId,
    metadata: RuntimeBindingCompatibilityMetadata,
    context: RenderContext
): MeasuringPointSwarmType | null {
    assertRuntimeBindingSupportsContextKind(bindingId, metadata, context.kind);

    if (metadata.supportedSwarmTypes === null) {
        return null;
    }

    if (context.kind !== 'measuring-point') {
        throw new Error(
            `Runtime binding "${bindingId}" requires a measuring-point context for SWARM compatibility.`
        );
    }

    return supportsSwarmType(metadata.supportedSwarmTypes, context.measuringPoint.swarmType)
        ? null
        : context.measuringPoint.swarmType;
}

export function isRuntimeBindingCompatibleWithContext(
    bindingId: BindingId,
    metadata: RuntimeBindingCompatibilityMetadata,
    context: RenderContext
): boolean {
    return getUnsupportedSwarmType(bindingId, metadata, context) === null;
}

export function assertRuntimeBindingCompatibleWithContext(
    bindingId: BindingId,
    metadata: RuntimeBindingCompatibilityMetadata,
    context: RenderContext
): void {
    const unsupportedSwarmType = getUnsupportedSwarmType(bindingId, metadata, context);
    if (unsupportedSwarmType === null) {
        return;
    }

    throw new Error(
        `Runtime binding "${bindingId}" does not support SWARM type "${unsupportedSwarmType}".`
    );
}
